import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/Images/logo.png";
// import readingPracticePdf from "../assets/reading.pdf"; // re-enable with the "வாசிப்புப் பயிற்சி" menu item
import * as bootstrap from "bootstrap";

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeLink, setActiveLink] = useState("");
  const [hoveredLink, setHoveredLink] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(null);

  const navLinks = [
    { name: "Home", href: "#home" },
    {
      name: "Classes",
      dropdown: [
        { name: "Tamil Classes in Blacktown", href: "/tamil-classes-blacktown/" },
        { name: "Programs & Levels", href: "/programs/" },
        { name: "How to Enrol", href: "/enrolment/" },
        { name: "Expression of Interest", href: "/eoi/" },
      ],
    },
    {
      name: "About",
      dropdown: [
        { name: "About WSTSC", href: "/about/" },
        { name: "Teachers & Volunteers", href: "#team" },
      ],
    },
    {
      name: "Resources",
      dropdown: [
        // {
        //   name: "கலாநிதி வேந்தனார் இளங்கோ ஞாபகார்த்த தமிழ்ப் புலமைச் சான்றிதழ்ப் பரீட்சை கடந்தகால வினாத்தாள்கள்",
        //   href: "/western-sydney-past-paper",
        // },
        {
          name: "Tamil : HSC Exam Resources and Past Papers",
          href: "https://www.vcaa.vic.edu.au/assessment/vce/examination-specifications-past-examinations-and-examination-reports/languages/tamil",
          external: true, // self-documenting (regex check also covers this)
        },
        {
          name: "தமிழ்மொழி வாழ்த்து",
          href: "/tamil-anthem/",
          lang: "ta",
        },
        // {
        //   name: "வாசிப்புப் பயிற்சி",
        //   href: readingPracticePdf,
        //   external: true,
        // },
      ],
    },
    { name: "Events", href: "/events/" },
    { name: "Contact Us", href: "/contact/" },
  ];

  const socialLinks = [
    {
      icon: "bi bi-facebook",
      href: "https://www.facebook.com/wstsc.org.au",
      platform: "facebook",
    },
    {
      icon: "bi bi-instagram",
      href: "https://www.instagram.com/wstsc.org.au/",
      platform: "instagram",
    },
    {
      icon: "bi bi-youtube",
      href: "https://www.youtube.com/@wstsc-org-au",
      platform: "youtube",
    },
    {
      icon: "bi bi-whatsapp",
      href: "https://wa.me/+61451112478",
      platform: "whatsapp",
    },
  ];

  // Helper: detect external URLs
  const isExternal = (url) => /^https?:\/\//i.test(url);

  // Real hrefs so crawlers can follow the menu; clicks still go through the
  // handlers below (smooth scrolling for home sections, SPA navigation).
  const toHref = (href) => (href.startsWith("#") ? `/${href}` : href);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
    };

    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      if (!isMobile) {
        setIsScrolled(scrollTop > 100);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isMobile]);

  // Set active link based on current location
  useEffect(() => {
    if (location.pathname === "/" && location.hash) {
      setActiveLink(location.hash);
    } else if (location.pathname === "/coming-soon") {
      setActiveLink("/coming-soon");
    } else if (location.pathname !== "/") {
      setActiveLink(
        location.pathname.endsWith("/")
          ? location.pathname
          : `${location.pathname}/`,
      );
    } else {
      setActiveLink("#home");
    }
  }, [location]);

  const closeOffcanvas = () => {
    const offcanvasEl = document.querySelector(".offcanvas.show");
    if (offcanvasEl) {
      const bsOffcanvas = bootstrap.Offcanvas.getInstance(offcanvasEl);
      bsOffcanvas?.hide();
    }
  };

  // Handle navigation
  const handleNavClick = (href, id, mobile = false) => {
    setActiveLink(id || href);
    setHoveredLink(""); // Clear hover state on click
    closeOffcanvas();
    setDropdownOpen(null);

    // Section links
    if (href.startsWith("#")) {
      const sectionId = href.substring(1);
      if (location.pathname === "/") {
        const section = document.getElementById(sectionId);
        if (section) section.scrollIntoView({ behavior: "smooth" });
      } else {
        // Home's sections shift height as it loads its own data (teachers,
        // events), so hand off the target instead of guessing a delay -
        // Home scrolls once that data has actually settled.
        navigate("/", { state: { scrollTo: sectionId } });
      }
      return;
    }

    // External URLs -> open in new tab
    if (isExternal(href)) {
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }

    // Internal routes
    navigate(href);
  };

  // Handle dropdown navigation
  const handleDropdownNavClick = (href, id, mobile = false, external = false) => {
    setActiveLink(id || href);
    setHoveredLink(""); // Clear hover state on click
    closeOffcanvas();
    setDropdownOpen(null);

    // Section links
    if (href.startsWith("#")) {
      const sectionId = href.substring(1);
      if (location.pathname === "/") {
        const section = document.getElementById(sectionId);
        if (section) section.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/", { state: { scrollTo: sectionId } });
      }
      return;
    }

    // External URLs (or files, e.g. PDFs) -> open in new tab
    if (external || isExternal(href)) {
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }

    // Internal routes
    navigate(href);
  };

  // Hover handlers
  const handleMouseEnter = (id) => setHoveredLink(id);
  const handleMouseLeave = () => setHoveredLink("");

  // Toggle dropdown for mobile only
  const toggleDropdown = (dropdownName) => {
    if (isMobile) {
      setDropdownOpen(dropdownOpen === dropdownName ? null : dropdownName);
    }
  };

  // Handle dropdown hover for desktop
  const handleDropdownHover = (dropdownName, action) => {
    if (!isMobile) {
      if (action === "enter") {
        setDropdownOpen(dropdownName);
      } else {
        setDropdownOpen(null);
      }
    }
  };

  const NavItems = ({ mobile = false }) => (
    <ul
      className={`navbar-nav d-flex justify-content-center gap-4 ${
        mobile ? "flex-column gap-3 text-center" : ""
      }`}
    >
      {navLinks.map((link, idx) => {
        if (link.dropdown) {
          const linkIdentifier = link.id || link.name;
          const isActive =
            activeLink === linkIdentifier ||
            link.dropdown.some((item) => activeLink === (item.id || item.href));
          const isHovered = hoveredLink === linkIdentifier;
          const isOpen = dropdownOpen === link.name;

          let textColor = "red-text";
          if (isActive || isHovered) textColor = "green-text";

          return (
            <li
              key={idx}
              className={`nav-item dropdown ${isOpen ? "show" : ""}`}
              onMouseEnter={
                !mobile
                  ? () => handleDropdownHover(link.name, "enter")
                  : undefined
              }
              onMouseLeave={
                !mobile
                  ? () => handleDropdownHover(link.name, "leave")
                  : undefined
              }
            >
              <button
                onClick={() => mobile && toggleDropdown(link.name)}
                onMouseEnter={() => !mobile && handleMouseEnter(linkIdentifier)}
                onMouseLeave={!mobile ? handleMouseLeave : undefined}
                className={`nav-link fw-semibold text-uppercase border-0 bg-transparent ${textColor} dropdown-toggle ${
                  mobile ? "fs-4 w-100" : ""
                }`}
                style={{
                  transition: "color 0.3s ease",
                  cursor: mobile ? "pointer" : "default",
                }}
                aria-expanded={isOpen}
              >
                {link.name}
              </button>
              <ul
                className={`dropdown-menu rounded-0 border-0 ${
                  isOpen ? "show" : ""
                } ${mobile ? "text-center" : ""}`}
                onMouseEnter={
                  !mobile
                    ? () => handleDropdownHover(link.name, "enter")
                    : undefined
                }
                onMouseLeave={
                  !mobile
                    ? () => handleDropdownHover(link.name, "leave")
                    : undefined
                }
              >
                {link.dropdown.map((dropdownItem, dropdownIdx) => {
                  const dropdownItemIdentifier =
                    dropdownItem.id || dropdownItem.href;
                  const isDropdownActive =
                    activeLink === dropdownItemIdentifier;
                  const isDropdownHovered =
                    hoveredLink === dropdownItemIdentifier;

                  let dropdownTextColor = "red-text";
                  if (isDropdownActive || isDropdownHovered)
                    dropdownTextColor = "green-text";

                  return (
                    <li key={dropdownIdx}>
                      <a
                        href={toHref(dropdownItem.href)}
                        lang={dropdownItem.lang}
                        {...(dropdownItem.external && {
                          target: "_blank",
                          rel: "noopener noreferrer",
                        })}
                        onClick={(e) => {
                          e.preventDefault();
                          handleDropdownNavClick(
                            dropdownItem.href,
                            dropdownItem.id || dropdownItem.href,
                            mobile,
                            dropdownItem.external,
                          );
                        }}
                        onMouseEnter={() =>
                          !mobile && handleMouseEnter(dropdownItemIdentifier)
                        }
                        onMouseLeave={!mobile ? handleMouseLeave : undefined}
                        className={`dropdown-item fw-semibold text-uppercase border-0 bg-transparent ${dropdownTextColor}`}
                        style={{
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          textAlign: mobile ? "center" : "left",
                        }}
                      >
                        {dropdownItem.name}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        } else {
          const linkIdentifier = link.id || link.href;
          const isActive = activeLink === linkIdentifier;
          const isHovered = hoveredLink === linkIdentifier;

          let textColor = "red-text";
          if (isActive || isHovered) textColor = "green-text";

          return (
            <li
              key={idx}
              className="nav-item"
              onMouseEnter={
                !mobile ? () => handleMouseEnter(linkIdentifier) : undefined
              }
              onMouseLeave={!mobile ? handleMouseLeave : undefined}
            >
              <a
                href={toHref(link.href)}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link.href, link.id || link.href, mobile);
                }}
                className={`nav-link fw-semibold text-uppercase border-0 bg-transparent ${textColor} ${
                  mobile ? "fs-4 w-100" : ""
                }`}
                style={{
                  transition: "color 0.3s ease",
                  cursor: "pointer",
                }}
              >
                {link.name}
              </a>
            </li>
          );
        }
      })}
    </ul>
  );

  return (
    <>
      <div className="container-fluid p-0">
        <div className="fixed-top">
          {/* Top Bar (Desktop only) - WITH OPACITY EFFECT */}
          <section
            className={`green-bg p-1 d-none d-lg-block transition-all duration-300 ${
              !isMobile && isScrolled
                ? "transform -translate-y-full opacity-0"
                : "transform translate-y-0 opacity-100"
            }`}
          >
            <div className="container p-0">
              <div className="row align-items-center">
                {/* Left Side: Social Icons */}
                <div className="col-md-8 d-flex align-items-center gap-4">
                  <div className="d-flex align-items-center gap-3">
                    {socialLinks.map((social, index) => (
                      <a
                        key={index}
                        href={social.href}
                        className="text-white social-icons"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Open ${social.platform}`}
                        title={social.platform}
                      >
                        <i
                          className={`${social.icon} fs-5`}
                          aria-hidden="true"
                        ></i>
                        <span className="visually-hidden">
                          {social.platform}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>

                {/* Right Side: Sign In */}
                <div className="col-md-4 d-flex justify-content-end gap-3">
                  <a
                    href="/dashboard"
                    className="text-white text-decoration-none fw-semibold social-icons"
                  >
                    Sign In
                  </a>
                  <span className="text-white fw-bold">|</span>
                  <a
                    href="/eoi/"
                    className="text-white text-decoration-none fw-semibold social-icons"
                  >
                    EOI Application
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Main Navigation - WITH OPACITY EFFECT */}
          <nav
            className={`navbar navbar-expand-lg navbar-light bg-white shadow-sm transition-all duration-300 ${
              !isMobile && isScrolled
                ? "transform -translate-y-full opacity-0"
                : "transform translate-y-0 opacity-100"
            } ${isMobile ? "fixed-top py-3" : "py-1"}`}
          >
            <div className="container p-0">
              {/* Mobile Header */}
              {isMobile && (
                <div className="d-flex align-items-center justify-content-between w-100 px-3 px-md-0">
                  <Link to="/" className="navbar-brand">
                    <img src={logo} alt="Western Sydney Tamil Study Centre home" height="50" width="237" />
                  </Link>
                  <button
                    className="navbar-toggler navbar-dark green-bg rounded-3 border-0 py-2"
                    type="button"
                    data-bs-toggle="offcanvas"
                    data-bs-target="#mainOffcanvas"
                    aria-controls="mainOffcanvas"
                    aria-label="Open navigation menu"
                  >
                    <span className="navbar-toggler-icon"></span>
                  </button>
                </div>
              )}

              {/* Desktop Header */}
              {!isMobile && (
                <div className="d-flex align-items-center justify-content-between w-100">
                  <Link to="/" className="navbar-brand">
                    <img src={logo} alt="Western Sydney Tamil Study Centre home" height="80" width="379" />
                  </Link>
                  <div className="d-none d-lg-flex align-items-center">
                    <NavItems />
                  </div>

                  <button
                    className="navbar-toggler d-lg-none navbar-dark green-bg rounded-3 border-0 py-2"
                    type="button"
                    data-bs-toggle="offcanvas"
                    data-bs-target="#mainOffcanvas"
                    aria-controls="mainOffcanvas"
                    aria-label="Open navigation menu"
                  >
                    <span className="navbar-toggler-icon"></span>
                  </button>
                </div>
              )}
            </div>
          </nav>

          {/* Fixed Navbar on Scroll (Desktop only) - APPEARS WHEN SCROLLED */}
          {isScrolled && !isMobile && (
            <section className="scroll-fixed-navbar position-fixed top-0 start-0 end-0 bg-white d-none d-lg-block shadow-sm transition-all duration-300">
              <div className="container p-0">
                <nav className="navbar navbar-expand-lg navbar-white py-1 d-flex align-items-center justify-content-between">
                  <Link to="/" className="navbar-brand p-0">
                    <img src={logo} alt="Western Sydney Tamil Study Centre home" height="70" width="332" />
                  </Link>
                  <div className="d-none d-lg-flex align-items-center justify-content-end">
                    <NavItems />
                  </div>
                </nav>
              </div>
            </section>
          )}
        </div>

        {/* Shared Offcanvas Menu */}
        <div
          className="offcanvas offcanvas-end w-100 p-3"
          tabIndex="-1"
          id="mainOffcanvas"
          data-bs-backdrop="false"
          data-bs-scroll="true"
        >
          <div className="offcanvas-header">
            <Link to="/" onClick={closeOffcanvas}>
              <img src={logo} alt="Western Sydney Tamil Study Centre home" height="60" width="285" />
            </Link>
            <button
              type="button"
              className="btn-close fs-1"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
            ></button>
          </div>
          <div className="offcanvas-body d-flex flex-column justify-content-center align-items-center gap-4">
            <NavItems mobile />

            {/* Sign In Button for Mobile */}
            <div className="d-lg-none d-flex flex-column text-center w-100 gap-5">
              <a
                href="/dashboard"
                className="red-text fs-4 text-uppercase fw-bold text-decoration-none"
                onClick={closeOffcanvas}
                style={{ minWidth: "120px" }}
              >
                Sign In
              </a>
              <a
                href="/eoi/"
                className="red-text text-decoration-none fw-semibold social-icons fs-4"
              >
                EOI
              </a>
            </div>

            <div className="pt-4 text-center w-100">
              <div className="d-flex align-items-center gap-4 justify-content-center">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    className="dark-text social-icon"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeOffcanvas}
                    aria-label={`Open ${social.platform}`}
                    title={social.platform}
                  >
                    <i className={`${social.icon} fs-5`} aria-hidden="true"></i>
                    <span className="visually-hidden">{social.platform}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
